import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

// Loads the immutable version history for one artifact type of a project and lets the UI
// view any past version. `viewPayload` is null when the latest version is selected (the
// stage then renders its live state); for an older version it holds that version's payload.
export function useVersions(projectId, artifactType) {
  const [versions, setVersions] = useState([]); // ascending version_no[]
  // Migration 044 added model_id / provider / parent_* to artifact_versions. `rows` keeps the
  // FULL row so the UI can say which model produced the version being viewed. `versions` stays
  // number[] on purpose: get_version requires a numeric version_no, and if this hook started
  // returning objects the request would fail, the error would be swallowed by the catch below,
  // and you would see v1 selected while v3's content stayed on screen.
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewPayload, setViewPayload] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.out('list_versions', { project_id: projectId });
      const mine = (r.versions || [])
        .filter((v) => v.artifact_type === artifactType)
        .sort((a, b) => a.version_no - b.version_no);
      const list = mine.map((v) => v.version_no);
      setRows(mine);
      setVersions(list);
      setSelected(list.length ? list[list.length - 1] : null);
      setViewPayload(null);
      return list;
    } catch {
      setRows([]); setVersions([]); setSelected(null); setViewPayload(null);
      return [];
    }
  }, [projectId, artifactType]);

  useEffect(() => { refresh(); }, [refresh]);

  const select = useCallback(async (n) => {
    setSelected(n);
    const latest = versions[versions.length - 1];
    if (n === latest) { setViewPayload(null); return; }
    try {
      const r = await api.out('get_version', { project_id: projectId, artifact_type: artifactType, version_no: n });
      setViewPayload(r.version?.payload ?? null);
    } catch { setViewPayload(null); }
  }, [projectId, artifactType, versions]);

  const latest = versions.length ? versions[versions.length - 1] : null;

  // The model that produced the version currently on screen — shaped for ServedModelBadge.
  // null for anything written before 044, which is the truth, not a bug: the caller falls back
  // to the per-function served-model lookup so those projects look exactly as they did before.
  const selectedModel = useMemo(() => {
    const row = rows.find((v) => v.version_no === selected);
    return row?.model_id ? { model_id: row.model_id, provider: row.provider ?? null } : null;
  }, [rows, selected]);

  return { versions, versionRows: rows, selected, latest, viewPayload, select, refresh, selectedModel };
}
